/**
 * R53 — the supported-language matrix, typed access to the single-source-of-truth JSON.
 *
 * `supported-languages.json` is the canonical config that BOTH halves read: the kiosk (this module)
 * and the agent-worker (Python `lang_matrix.py`). Never hand-maintain a parallel list — edit the JSON.
 *
 * Matrix logic (see the JSON `_meta`):
 *   AUTO        — Deepgram nova-3 `multi` code-switch ∩ ElevenLabs v2.5. Auto-detected, no button.
 *   PINNED      — Deepgram can pin single-language ∩ ElevenLabs v2.5. Needs the language button.
 *   UNSUPPORTED — ElevenLabs v2.5 cannot voice it → not offered (TTS is the hard floor).
 */
import matrix from './supported-languages.json';

export type LangTier = 'AUTO' | 'PINNED' | 'UNSUPPORTED';

export type LanguageEntry = {
  code: string;
  name: string;
  stt_auto: boolean;
  stt_pinned: boolean;
  el_v25: boolean;
  el_v2: boolean;
  tier: LangTier;
};

/** The full ordered matrix — AUTO languages first, then PINNED, as authored in the JSON. */
export const LANGUAGE_MATRIX: LanguageEntry[] = matrix.languages as LanguageEntry[];

/** The TTS model map: active-language code → ElevenLabs model. `*` is the non-English fallback. */
export const TTS_MODEL_MAP: { default: string; map: Record<string, string> } = {
  default: matrix._meta.tts_model_map.default,
  map: matrix._meta.tts_model_map.map,
};

/** Resolve the ElevenLabs model for an active language code (primary subtag). */
export function ttsModelForLang(code: string | null | undefined): string {
  const base = (code ?? '').toLowerCase().split('-')[0] ?? '';
  return TTS_MODEL_MAP.map[base] ?? TTS_MODEL_MAP.map['*'] ?? TTS_MODEL_MAP.default;
}
